import type { AgenticLogicRuleDto } from "@intelligems/ig-types/dtos/agentic-logic/rules";
import type { AgenticLogicRulePublishJobDto } from "@intelligems/ig-types/dtos/agentic-logic/publish";
import type { AgenticLogicTemplateSummaryDto } from "@intelligems/ig-types/functions/agentic-logic/templates/list";

import {
  rememberAgenticLogicPublishJob,
  useAgenticLogicPublishProgress,
} from "@src/api/agentic-logic/agentic-logic-publish-progress";
import { useCreateAgenticLogicRule } from "@src/api/agentic-logic/create-agentic-logic-rule";
import { usePublishAgenticLogicRule } from "@src/api/agentic-logic/publish-agentic-logic-rule";
import { useUpdateAgenticLogicRule } from "@src/api/agentic-logic/update-agentic-logic-rule";

import type { RuleSaveAction } from "../workspace/publishOnSave";

import {
  type PersistTemplateConfigInput,
  type TemplateConfigWorkspaceAdapter,
  type TemplateConfigWorkspaceMode,
  useTemplateConfigWorkspace,
} from "./useTemplateConfigWorkspace";

export type TemplateRuleWorkspace = ReturnType<typeof useTemplateRuleWorkspace>;

/**
 * The API-bound template-rule editor: the pure config workspace wired to the
 * create/update/publish endpoints, plus the publish job it follows.
 *
 * Lifted out of {@link TemplateConfigWorkspace} so the rule detail page can own
 * the write actions in its page frame while the workspace body — one editor
 * buffer, one publish job — stays a single source of truth. The standalone
 * create flow calls the same hook.
 */
export const useTemplateRuleWorkspace = ({
  mode,
  template,
  latestPublishJob,
  onCreated,
}: {
  mode: TemplateConfigWorkspaceMode;
  template: AgenticLogicTemplateSummaryDto;
  latestPublishJob?: AgenticLogicRulePublishJobDto | null;
  onCreated: (rule: AgenticLogicRuleDto) => void;
}) => {
  const createMutation = useCreateAgenticLogicRule();
  const updateMutation = useUpdateAgenticLogicRule();
  const publishMutation = usePublishAgenticLogicRule();
  const ruleId = mode.kind === "edit" ? mode.rule.id : undefined;
  const publishProgress = useAgenticLogicPublishProgress(
    ruleId,
    latestPublishJob
  );

  const adapter: TemplateConfigWorkspaceAdapter = {
    // No templateVersion in either body: the save endpoint validates the
    // params against the live template and stamps its version itself.
    persist: async (input: PersistTemplateConfigInput) => {
      if (input.kind === "create") {
        const response = await createMutation.mutateAsync({
          body: {
            name: input.name,
            description: template.description,
            managedBy: "builder",
            templateId: input.templateId,
            templateParams: input.templateParams,
            settings: input.settings,
          },
          pathParams: undefined,
          queryParams: undefined,
        });
        return response.rule;
      }

      const response = await updateMutation.mutateAsync({
        body: {
          name: input.name,
          templateParams: input.templateParams,
          // Replaces the stored object whole — the same contract the PATCH
          // route documents. A template rule's Save writes its four answers
          // alongside its params because they are one setup.
          settings: input.settings,
        },
        pathParams: { ruleId: input.ruleId },
        queryParams: undefined,
      });
      return response.rule;
    },
  };

  const workspace = useTemplateConfigWorkspace({ mode, adapter });

  const isPublishing =
    publishMutation.isPending || publishProgress.isPublishing;

  /**
   * Runs the publish pipeline against what is PERSISTED, and follows the job
   * it returns. Shared by the two ways a template rule deploys — the save that
   * deploys, and the standalone Publish on the unpublished-changes callout —
   * so both hand the same job to the same progress reader.
   */
  const runPublish = async (publishRuleId: string) => {
    try {
      const { publishJobId } = await publishMutation.mutateAsync({
        body: {},
        pathParams: { ruleId: publishRuleId },
        queryParams: undefined,
      });
      // On the create screen this hook is bound to no rule, so hand the job to
      // the rule page the caller is about to navigate to instead.
      if (mode.kind === "create") {
        rememberAgenticLogicPublishJob(publishRuleId, publishJobId);
      } else {
        publishProgress.trackJob(publishJobId);
      }
    } catch {
      // The mutation already surfaced the failure; the rule is saved either
      // way, so the create flow must still hand the caller its new rule.
    }
  };

  /**
   * Save, and — unless told otherwise — deploy: the config a save writes is
   * the config that serves, so for a rule nobody is looking at, persisting and
   * publishing is one act rather than two the merchant has to sequence.
   *
   * `"save_draft_only"` is the exception, and it is not about this rule's
   * flavour: a rule attached to a STARTED experience must not change what
   * shoppers see because someone pressed the page's most obvious button
   * (`publishOnSave.ts`). The configuration is still persisted; the deploy
   * waits for the explicit Publish on the unpublished-changes callout.
   *
   * A publish that fails still leaves the configuration saved — the publish
   * job reports why, and saving again retries it. This is also the deploy the
   * chat does not make: Atlas writes the rule row and stops, so a chat edit to
   * a serving rule waits here too (see `useUnpublishedRuleChanges`).
   */
  const save = async (action: RuleSaveAction = "save_and_publish") => {
    const rule = await workspace.persist();
    if (!rule) {
      throw new Error("Rule could not be saved.");
    }

    if (action === "save_and_publish") {
      await runPublish(rule.id);
    }

    if (mode.kind === "create") {
      onCreated(rule);
    }
  };

  /** Deploy what is already persisted. The callout's Publish, and nothing else. */
  const publish = async () => {
    if (ruleId === undefined) {
      return;
    }
    await runPublish(ruleId);
  };

  return {
    workspace,
    template,
    mode,
    publishJob: publishProgress.job,
    /** `false` for a run hydrated from rule detail: history, not news. */
    isSessionRun: publishProgress.isSessionRun,
    isPublishing,
    /** `true` from the first byte of the save until the publish is queued. */
    isSaving: workspace.isPersisting || publishMutation.isPending,
    save,
    publish,
  };
};
